// ChatWindow.jsx — FINAL ENTERPRISE STABLE VERSION
//---------------------------------------------------

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useChat } from "../../hooks/useChat";
import { db, storage } from "../../../../integrations/firebase";
import { MessageService } from "../../services/MessageService";
import {
    ref,
    onValue,
    set as firebaseSet,
    get,
    remove,
    update,
    serverTimestamp
} from "firebase/database";
import {
    ref as sRef,
    uploadBytesResumable,
    getDownloadURL
} from "firebase/storage";
import {
    Phone,
    Video,
    MoreVertical,
    MessageSquare,
    X,
    ChevronLeft,
    Minimize2,
    Users,
    Send,
    Palette
} from "lucide-react";
import FileUploadProgress from "./FileUploadProgress";
import { useMessageWindow } from "../../hooks/useMessageWindow";
import { searchConversation } from "../../services/ChatSearchService";
import { useChannel } from "../../hooks/useChannel";
import ChannelInfoPanel from "../Channels/ChannelInfoPanel";
import { ChannelGlyph } from "../Channels/ChannelDirectoryModal";
import { AckBar, AnnounceModal } from "../Channels/Announcements";
import { useToast } from "../../../../shared/ui/Toast";

// Sub-components
import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";
import ContactInfoPanel from "../ChatInfo/ContactInfoPanel";
import GroupInfoPanel from "../ChatInfo/GroupInfoPanel";
import PinnedMessageBar from "./PinnedMessageBar";

// Services
import { GroupService } from "../../services/GroupService";

// Modals
import TaskCreationModal from "../Modals/TaskCreationModal.jsx";
import ScheduleModal from "../Modals/ScheduleModal.jsx";

// Chat Theme Presets
const CHAT_THEMES = {
    indigo: { label: 'Indigo', preview: '#6366F1', gradient: 'linear-gradient(135deg, #6366F1, #8B5CF6)', bg: '#F7F9FB', pattern: 'rgba(99,102,241,0.03)', myBubble: 'sent-bubble', myText: 'text-white', otherBubble: 'received-bubble', otherText: 'text-slate-700' },
    blue: { label: 'Ocean', preview: '#3B82F6', gradient: 'linear-gradient(135deg, #3B82F6, #06B6D4)', bg: '#F0F9FF', pattern: 'rgba(59,130,246,0.03)', myBubble: 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-md', myText: 'text-white', otherBubble: 'received-bubble', otherText: 'text-slate-700' },
    rose: { label: 'Rose', preview: '#F43F5E', gradient: 'linear-gradient(135deg, #F43F5E, #FB923C)', bg: '#FFF1F2', pattern: 'rgba(244,63,94,0.03)', myBubble: 'bg-gradient-to-br from-rose-500 to-orange-400 text-white shadow-md', myText: 'text-white', otherBubble: 'received-bubble', otherText: 'text-slate-700' },
    emerald: { label: 'Emerald', preview: '#10B981', gradient: 'linear-gradient(135deg, #10B981, #14B8A6)', bg: '#ECFDF5', pattern: 'rgba(16,185,129,0.03)', myBubble: 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md', myText: 'text-white', otherBubble: 'received-bubble', otherText: 'text-slate-700' },
    sunset: { label: 'Sunset', preview: '#F59E0B', gradient: 'linear-gradient(135deg, #F59E0B, #EF4444)', bg: '#FFFBEB', pattern: 'rgba(245,158,11,0.03)', myBubble: 'bg-gradient-to-br from-amber-500 to-red-500 text-white shadow-md', myText: 'text-white', otherBubble: 'received-bubble', otherText: 'text-slate-700' },
    dark: { label: 'Dark', preview: '#1E1B4B', gradient: 'linear-gradient(135deg, #312E81, #4F46E5)', bg: '#1E1B4B', pattern: 'rgba(255,255,255,0.03)', myBubble: 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md', myText: 'text-white', otherBubble: 'bg-slate-800/80 text-slate-100 border border-slate-700/50', otherText: 'text-slate-100' },
};

const ChatWindow = ({
    activeChat,
    activeCall,
    onStartCall,
    onBack, // Mobile back
    onClose, // Desktop close
    onMinimize, // Desktop minimize
    isFloating = false,
    isFullPage = false,
    isMobile = false
}) => {
    const { currentUser, startCall, allUsers, markChatRead } = useChat();
    const navigate = useNavigate();
    const { showToast } = useToast();
    // Default to true for admin/existing users without permissions
    const permissions = currentUser?.permissions || { canReplyChat: true, canInitiateCall: true };
    // The newest messages live, older ones on request — see hooks/useMessageWindow.
    const {
        messages, loading, ready: messagesReady, error: messagesError,
        hasOlder, loadingOlder, loadOlder, seek,
    } = useMessageWindow(activeChat?.chatId);

    // Channels: membership and posting rights come from channels/{id}, which only the
    // server writes. The database rules enforce posting; this drives what the UI offers.
    const myUid = currentUser?.uid || currentUser?.id;
    const isChannel = activeChat?.type === 'channel' || activeChat?.isChannel === true;
    const isMultiPerson = isChannel || activeChat?.type === 'group' || activeChat?.isGroup === true;
    const {
        channel, isAdmin: isChannelAdmin, isOwner: isChannelOwner, canPost: canPostInChannel,
    } = useChannel(activeChat?.chatId, isChannel, myUid);
    const channelMemberCount = channel?.members ? Object.values(channel.members).filter(v => v === true).length : (activeChat?.memberCount || 0);
    const [showAnnounce, setShowAnnounce] = useState(false);
    const [inputText, setInputText] = useState("");
    const fileInputRef = useRef(null);
    const [deleteConfirmMsg, setDeleteConfirmMsg] = useState(null);
    const [canPinMessages, setCanPinMessages] = useState(true);
    const messagesContainerRef = useRef(null);

    // In-conversation search (server-side, over the full history).
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchState, setSearchState] = useState({ status: 'idle', hits: [], total: 0, truncated: false, error: null });

    // Upload State
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [replyTo, setReplyTo] = useState(null);

    // Feature State
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedMessageForAction, setSelectedMessageForAction] = useState(null);
    const [showContactInfo, setShowContactInfo] = useState(false);
    const [showThemePicker, setShowThemePicker] = useState(false);
    const [chatTheme, setChatTheme] = useState(() => {
        try { return localStorage.getItem('chatTheme') || 'indigo'; } catch { return 'indigo'; }
    });
    const [chatWallpaper, setChatWallpaper] = useState(() => {
        try { return localStorage.getItem('chatWallpaper') || ''; } catch { return ''; }
    });
    const themePickerRef = useRef(null);
    const wallpaperInputRef = useRef(null);

    const handleThemeChange = (themeKey) => {
        setChatTheme(themeKey);
        try { localStorage.setItem('chatTheme', themeKey); } catch {}
        // Dispatch event so sidebar can pick up the change
        window.dispatchEvent(new Event('chatThemeChanged'));
        setShowThemePicker(false);
    };

    const handleWallpaperUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            setChatWallpaper(dataUrl);
            try { localStorage.setItem('chatWallpaper', dataUrl); } catch {}
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const clearWallpaper = () => {
        setChatWallpaper('');
        try { localStorage.removeItem('chatWallpaper'); } catch {}
    };

    const currentTheme = CHAT_THEMES[chatTheme] || CHAT_THEMES.indigo;

    // Audio recording state
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleStartRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await handleAudioUpload(audioBlob);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (error) {
            console.error("Microphone error:", error);
            showToast("Could not access microphone. Please allow permissions.", "error");
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const handleAudioUpload = async (audioBlob) => {
        setIsUploading(true);
        try {
            const storagePath = `chat_audio/${activeChat.chatId}/${Date.now()}.webm`;
            const storageRef = sRef(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, audioBlob);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Audio Upload Error:", error);
                    setIsUploading(false);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    await sendMessage("audio", downloadURL, "Voice Message", null);
                    setIsUploading(false);
                    setUploadProgress(0);
                }
            );
        } catch (err) {
            console.error(err);
            setIsUploading(false);
        }
    };

    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // Messages are loaded by useMessageWindow (above). This used to be an unbounded
    // onValue on conversations/{id}/messages: every open conversation downloaded its
    // entire history, and re-received it, for as long as it stayed open.

    // --- Reset Unread + Mark Messages as Seen ---
    useEffect(() => {
        if (!activeChat?.chatId || !currentUser?.uid) return;
        const userId = currentUser.uid || currentUser.id;
        const chatId = activeChat.chatId;

        // Reset unread count
        firebaseSet(ref(db, `conversations/${chatId}/unread/${userId}`), 0)
            .catch(e => console.warn("Failed to reset unread:", e));
    }, [activeChat?.chatId, currentUser?.uid, currentUser?.id]);

    // Mark received messages as seen when they appear
    useEffect(() => {
        if (!activeChat?.chatId || !currentUser?.uid || messages.length === 0) return;
        const userId = currentUser.uid || currentUser.id;
        const chatId = activeChat.chatId;

        // One call: marks the unseen incoming messages AND clears this reader's unread
        // counter in the same multi-path update. The counter used to be left alone here,
        // so a conversation you were actively reading kept its badge for ever.
        MessageService.markIncomingSeen(chatId, userId, messages)
            .then(() => markChatRead?.(chatId))      // clear the local badge immediately
            .catch(e => console.warn('Failed to mark messages as seen:', e));
    }, [messages, activeChat?.chatId, currentUser?.uid, currentUser?.id, markChatRead]);

    // --- Compute canPin permission for this chat ---
    useEffect(() => {
        if (!activeChat?.chatId || !currentUser?.uid) {
            setCanPinMessages(true); // Default true for 1:1
            return;
        }
        // A channel: only its admins pin, so the pinned bar stays an official record.
        if (isChannel) {
            setCanPinMessages(isChannelAdmin);
            return;
        }
        if (activeChat.type !== 'group') {
            setCanPinMessages(true);
            return;
        }
        // Group chat — check role
        GroupService.canPin(activeChat.chatId, currentUser.uid)
            .then(can => setCanPinMessages(can))
            .catch(() => setCanPinMessages(false));
    }, [activeChat?.chatId, activeChat?.type, currentUser?.uid, isChannel, isChannelAdmin]);

    //---------------------------------------------------
    // LISTEN FOR TYPING STATUS
    //---------------------------------------------------
    const [typingUsers, setTypingUsers] = useState([]);

    useEffect(() => {
        if (!activeChat?.chatId || !currentUser?.uid) return;

        const typingRef = ref(db, `conversations/${activeChat.chatId}/typing`);
        const onTypingUpdate = (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const typers = Object.keys(data)
                    .filter(uid => uid !== currentUser.uid && uid !== currentUser.id); // Exclude self
                setTypingUsers(typers);
            } else {
                setTypingUsers([]);
            }
        };

        const unsubscribe = onValue(typingRef, onTypingUpdate);
        return () => unsubscribe();
    }, [activeChat?.chatId, currentUser?.uid]);

    //---------------------------------------------------
    // AUTO SCROLL (Smart)
    //---------------------------------------------------
    // Scroll to the bottom only when the NEWEST message changes (it arrived or was sent),
    // and only if the reader was already near the bottom — someone reading back through
    // history must not be yanked down by every incoming message. This used to run on any
    // change to `messages`, which also meant a receipt tick or a reaction scrolled you away.
    const newestIdRef = useRef(null);
    const prependAnchorRef = useRef(null);       // scrollHeight before older messages were added
    useEffect(() => {
        newestIdRef.current = null;
    }, [activeChat?.chatId]);

    useLayoutEffect(() => {
        const box = messagesContainerRef.current;
        if (!box || messages.length === 0) return;

        // Older messages were just prepended: keep what the reader was looking at in place.
        if (prependAnchorRef.current !== null) {
            box.scrollTop += box.scrollHeight - prependAnchorRef.current;
            prependAnchorRef.current = null;
            return;
        }

        const newest = messages[messages.length - 1]?.id;
        if (newest === newestIdRef.current) return;
        const first = newestIdRef.current === null;
        newestIdRef.current = newest;
        const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 160;
        const mine = messages[messages.length - 1]?.senderId === (currentUser?.uid || currentUser?.id);
        if (first) box.scrollTop = box.scrollHeight;            // opening: land on the latest, no animation
        else if (nearBottom || mine) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, currentUser?.uid, currentUser?.id]);

    const handleLoadOlder = useCallback(async () => {
        const box = messagesContainerRef.current;
        if (box) prependAnchorRef.current = box.scrollHeight;
        const added = await loadOlder();
        if (!added) prependAnchorRef.current = null;
    }, [loadOlder]);

    // Reaching the top of the list loads the previous page, like any messenger.
    const handleMessagesScroll = useCallback((e) => {
        if (e.currentTarget.scrollTop < 80 && hasOlder && !loadingOlder) handleLoadOlder();
    }, [hasOlder, loadingOlder, handleLoadOlder]);


    //---------------------------------------------------
    // TYPING INDICATOR HANDLER
    //---------------------------------------------------
    const handleTyping = (e) => {
        const text = e.target.value;
        setInputText(text);
        if (!activeChat?.chatId || !currentUser?.uid) return;

        // Throttled typing indicator
        if (!typingTimeoutRef.current) {
            const typingRef = ref(db, `conversations/${activeChat.chatId}/typing/${currentUser.uid}`);
            firebaseSet(typingRef, true);
            typingTimeoutRef.current = setTimeout(() => {
                firebaseSet(typingRef, null);
                typingTimeoutRef.current = null;
            }, 2000);
        }
    };

    //---------------------------------------------------
    // SEND MESSAGE
    //---------------------------------------------------
    const sendMessage = async (type = "text", fileUrl = null, fileName = null, fileSize = null, scheduledTimestamp = null) => {
        if ((type === "text" && !inputText.trim()) || (!inputText.trim() && !fileUrl)) return;

        const msgData = {
            senderId: currentUser.uid || currentUser.id,
            senderName: currentUser.displayName || currentUser.name || "User",
            text: type === "text" ? inputText : (fileName || "File"),
            type: type,
            fileUrl: fileUrl,
            fileName: fileName,
            fileSize: fileSize,
            replyTo: replyTo ? { id: replyTo.id, text: replyTo.text, sender: replyTo.senderId } : null,
        };

        try {
            if (scheduledTimestamp) {
                await MessageService.scheduleMessage(activeChat.chatId, msgData, scheduledTimestamp);
                showToast(`Message scheduled for ${new Date(scheduledTimestamp).toLocaleString()}`, 'success');
            } else {
                await MessageService.sendMessage(activeChat.chatId, msgData);
            }

            if (!scheduledTimestamp) {
                setInputText("");
                setReplyTo(null);
            }

        } catch (error) {
            console.error("Send Error:", error);
            showToast("Failed to send message: " + error.message, "error");
        }
    };

    //---------------------------------------------------
    // ACTION HANDLERS
    //---------------------------------------------------
    //---------------------------------------------------
    // ACTION HANDLERS
    //---------------------------------------------------
    const handlePinMessage = useCallback(async (msg) => {
        const userId = currentUser?.uid || currentUser?.id;
        const result = await MessageService.pinMessage(activeChat.chatId, msg, {
            uid: userId,
            name: currentUser?.name || currentUser?.displayName || currentUser?.email
        });
        if (result.success) {
            showToast('Message pinned', 'success');
        } else {
            showToast(result.error || 'Failed to pin message', 'error');
        }
    }, [activeChat, currentUser, showToast]);

    const handleUnpinMessage = useCallback(async (msg) => {
        const userId = currentUser?.uid || currentUser?.id;
        const result = await MessageService.unpinMessage(activeChat.chatId, msg.id, userId);
        if (result.success) {
            showToast('Message unpinned', 'info');
        } else {
            showToast(result.error || 'Failed to unpin message', 'error');
        }
    }, [activeChat, currentUser, showToast]);

    // Scroll to a specific message (for pinned message click)
    // Jump to a message — from the pinned bar or a search result. Only the newest messages
    // are loaded, so an older target is paged in first; before, the jump silently did
    // nothing whenever the message was not already on the page.
    const scrollToMessage = useCallback(async (messageId) => {
        const found = await seek(messageId);
        if (!found) {
            showToast('That message is too far back to open here, or it was removed.', 'error');
            return;
        }
        // Wait a frame for the paged-in messages to render.
        requestAnimationFrame(() => {
            const el = document.getElementById(`message-${messageId}`);
            if (!el) return;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.transition = 'background-color 0.3s ease';
            el.style.backgroundColor = 'rgba(99, 102, 241, 0.12)';
            setTimeout(() => { el.style.backgroundColor = ''; }, 2000);
        });
    }, [seek, showToast]);

    // ---- Search ----------------------------------------------------------------------
    const searchAbortRef = useRef(null);
    const closeSearch = useCallback(() => {
        searchAbortRef.current?.abort();
        setSearchOpen(false);
        setSearchText('');
        setSearchState({ status: 'idle', hits: [], total: 0, truncated: false, error: null });
    }, []);
    useEffect(() => { closeSearch(); }, [activeChat?.chatId, closeSearch]);

    const runSearch = useCallback(async (q) => {
        const chatId = activeChat?.chatId;
        if (!chatId || !q.trim()) {
            setSearchState({ status: 'idle', hits: [], total: 0, truncated: false, error: null });
            return;
        }
        searchAbortRef.current?.abort();
        const ctl = new AbortController();
        searchAbortRef.current = ctl;
        setSearchState(s => ({ ...s, status: 'loading', error: null }));
        try {
            const r = await searchConversation(chatId, q.trim(), { signal: ctl.signal });
            if (ctl.signal.aborted) return;
            setSearchState({ status: 'done', ...r, error: null });
        } catch (e) {
            if (e?.name === 'AbortError') return;
            setSearchState({ status: 'error', hits: [], total: 0, truncated: false, error: e.message });
        }
    }, [activeChat?.chatId]);

    // Opened from an "acknowledge this" reminder: jump to that message once it can be found.
    // (Declared after scrollToMessage — referencing a const before its line throws.)
    const focusedRef = useRef(null);
    useEffect(() => {
        const target = activeChat?.focusMessageId;
        if (!target || !messagesReady || focusedRef.current === `${activeChat.chatId}:${target}`) return;
        focusedRef.current = `${activeChat.chatId}:${target}`;
        scrollToMessage(target);
    }, [activeChat?.focusMessageId, activeChat?.chatId, messagesReady, scrollToMessage]);

    // Search as you type, but not on every keystroke.
    useEffect(() => {
        if (!searchOpen) return undefined;
        const t = setTimeout(() => runSearch(searchText), 350);
        return () => clearTimeout(t);
    }, [searchText, searchOpen, runSearch]);

    const handleDeleteMessage = useCallback(async (msg) => {
        setDeleteConfirmMsg(msg);
    }, []);

    const confirmDeleteMessage = useCallback(async (deleteType) => {
        if (!deleteConfirmMsg) return;
        const userId = currentUser?.uid || currentUser?.id;
        await MessageService.deleteMessage(activeChat.chatId, deleteConfirmMsg.id, userId, deleteType);
        setDeleteConfirmMsg(null);
        showToast(deleteType === 'for_everyone' ? 'Message deleted for everyone' : 'Message deleted for you', 'info');
    }, [activeChat, deleteConfirmMsg, currentUser, showToast]);

    const handleRetryMessage = useCallback(async (msg) => {
        // Re-send with same clientMessageId
        const result = await MessageService.sendMessage(activeChat.chatId, {
            ...msg,
            status: 'sending',
            retryCount: (msg.retryCount || 0) + 1
        });
        if (!result.success) {
            showToast('Retry failed: ' + (result.error || 'Unknown error'), 'error');
        }
    }, [activeChat, showToast]);

    const handleForwardMessage = useCallback((msg) => {
        // Since there isn't a dedicated Forward Modal yet, we copy to clipboard
        // so the user can paste it in another chat.
        const textToCopy = msg.type === 'text' ? msg.text : `[${msg.type.toUpperCase()}] ${msg.fileUrl || ''}`;
        navigator.clipboard.writeText(textToCopy);
        showToast('Message copied to clipboard for forwarding', 'success');
    }, [showToast]);

    const handleOpenTaskModal = useCallback((msg) => {
        setSelectedMessageForAction(msg);
        setShowTaskModal(true);
    }, []);

    // Reminder Handler
    const handleReminder = useCallback((msg) => {
        setSelectedMessageForAction(msg); // Re-use this state or add a new one? Re-using is fine if modes match
        // Or better, set a specific flag
        setShowScheduleModal(true);
    }, []);

    // Create Task (From Modal)
    const handleCreateTask = async (taskData) => {
        if (!selectedMessageForAction) return;
        await MessageService.createTaskFromMessage({
            messageId: selectedMessageForAction.id,
            messageText: selectedMessageForAction.text,
            chatId: activeChat.chatId,
            createdBy: currentUser.uid,
            assignedTo: taskData.assignedTo || currentUser.uid,
            dueDate: taskData.dueDate
        });
        setShowTaskModal(false);
        setSelectedMessageForAction(null);
    };

    // Handle Schedule/Reminder Confirm
    const handleScheduleConfirm = async (timestamp) => {
        // If we have a selected message, it is a REMINDER for that message
        if (selectedMessageForAction && !showTaskModal) {
            // Note: showTaskModal check is redundant if we manage state well, but safe.
            // Actually, handleOpenTaskModal sets selectedMessageForAction too.
            // We need to distinguish Task vs Reminder.
            // But Task opens TaskModal. Reminder opens ScheduleModal.
            // So if ScheduleModal is open and we have selectedMessageForAction, it is a reminder.

            await MessageService.scheduleReminder(
                currentUser.uid,
                selectedMessageForAction.id,
                "Reminder for message",
                timestamp
            );
            showToast(`Reminder set for ${new Date(timestamp).toLocaleString()}`, 'success');
            setSelectedMessageForAction(null);
        } else {
            // New Scheduled Message
            await sendMessage(inputText ? "text" : "file", null, null, null, timestamp);
        }
        setShowScheduleModal(false);
    };

    // ... (rest of file)

    // In render:
    // <ScheduleModal ... onSchedule={handleScheduleConfirm} />
    // <MessageBubble ... onReminder={handleReminder} ... />

    // Emoji Reactions handler
    const handleReaction = useCallback(async (msg, emoji) => {
        if (!activeChat?.chatId || !msg?.id) return;
        const userId = currentUser?.uid || currentUser?.id;
        if (!userId) return;
        const reactionPath = `conversations/${activeChat.chatId}/messages/${msg.id}/reactions/${emoji}/${userId}`;
        try {
            const snap = await get(ref(db, reactionPath));
            if (snap.exists()) {
                await remove(ref(db, reactionPath));
            } else {
                await firebaseSet(ref(db, reactionPath), true);
            }
        } catch (err) {
            console.error('Reaction error:', err);
        }
    }, [activeChat?.chatId, currentUser]);

    // Update the return block below

    //---------------------------------------------------
    // FILE UPLOAD HANDLER
    //---------------------------------------------------
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const isImage = file.type.startsWith("image/");
            const msgType = isImage ? "image" : "file";
            const storagePath = `chat_uploads/${activeChat.chatId}/${Date.now()}_${file.name}`;
            const storageRef = sRef(storage, storagePath);

            const uploadTask = uploadBytesResumable(storageRef, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Upload Error:", error);
                    setIsUploading(false);
                    showToast("Upload failed!", "error");
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    await sendMessage(msgType, downloadURL, file.name, (file.size / 1024).toFixed(1) + " KB");
                    setIsUploading(false);
                    setUploadProgress(0);
                }
            );
        } catch (err) {
            console.error(err);
            setIsUploading(false);
        }
    };

    // Handle image paste from clipboard
    const handlePasteImage = async (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setIsUploading(true);
        try {
            const fileName = `pasted_image_${Date.now()}.png`;
            const storagePath = `chat_uploads/${activeChat.chatId}/${Date.now()}_${fileName}`;
            const storageRef = sRef(storage, storagePath);

            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setUploadProgress(progress);
                },
                (error) => {
                    console.error("Paste Upload Error:", error);
                    setIsUploading(false);
                    showToast("Paste upload failed!", "error");
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    await sendMessage("image", downloadURL, fileName, (file.size / 1024).toFixed(1) + " KB");
                    setIsUploading(false);
                    setUploadProgress(0);
                    showToast("Image pasted & sent!", "success");
                }
            );
        } catch (err) {
            console.error(err);
            setIsUploading(false);
        }
    };


    if (!activeChat || !currentUser) {
        return (
            <div className="h-full w-full flex items-center justify-center" style={{ backgroundColor: '#f0f2f5', backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(0,0,0,0.012) 2px, transparent 0)', backgroundSize: '50px 50px' }}>
                <div className="text-center max-w-sm">
                    {/* Logo / Brand */}
                    <div className="relative mx-auto w-28 h-28 mb-6">
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-violet-500 rounded-3xl rotate-6 opacity-20" />
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-3xl flex items-center justify-center shadow-xl shadow-indigo-500/20">
                            <MessageSquare size={48} className="text-white" />
                        </div>
                    </div>
                    <h2 className="text-xl font-bold text-slate-800 mb-2">Team Daksha 360</h2>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">
                        Send and receive messages without keeping your phone online.<br/>
                        Select a chat to start messaging.
                    </p>
                    <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                        End-to-end connected
                    </div>
                </div>
            </div>
        );
    }

    //---------------------------------------------------
    // RENDER MAIN LAYOUT
    //---------------------------------------------------
    return (
        <div className="h-full w-full flex flex-row overflow-hidden bg-transparent gap-4">
            {/* MAIN CHAT AREA */}
            <div className={`flex flex-col h-full relative flex-1 bg-white overflow-hidden ${isFloating ? '' : 'rounded-3xl shadow-floating'}`}>
                <div className="flex-1 flex flex-col relative overflow-hidden bg-transparent" style={{
                    backgroundColor: currentTheme.bg,
                    backgroundImage: chatWallpaper
                        ? `url(${chatWallpaper})`
                        : `radial-gradient(circle at 25px 25px, ${currentTheme.pattern} 2px, transparent 0)`,
                    backgroundSize: chatWallpaper ? 'cover' : '50px 50px',
                    backgroundPosition: chatWallpaper ? 'center' : undefined,
                    backgroundRepeat: chatWallpaper ? 'no-repeat' : undefined,
                }}>

                    {/* HEADER — Floating Card */}
                    <div
                        onClick={() => setShowContactInfo(!showContactInfo)}
                        className={`chat-header flex justify-between items-center sticky z-20 gap-2 ${isFloating
                            ? 'h-14 px-3 bg-white border-b border-slate-100 top-0'
                            : 'h-16 glass-morphism rounded-3xl shadow-floating mx-3 mt-3 px-4 top-3'}`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                        {/* min-w-0 lets the name truncate instead of pushing the action
                            buttons out of the window — that overflow is what hid the
                            Close button in a 360-420px docked window. */}
                        <div className={`flex items-center min-w-0 flex-1 ${isFloating ? 'gap-2' : 'gap-3'}`}>
                            {onBack && (
                                <button onClick={(e) => { e.stopPropagation(); onBack(); }} className="md:hidden p-1 -ml-1 text-slate-400 hover:bg-slate-100 rounded-full">
                                    <ChevronLeft size={24} />
                                </button>
                            )}
                            <div className="relative flex-shrink-0">
                                <div className={`rounded-full overflow-hidden ${isFloating ? 'w-8 h-8' : 'w-10 h-10'}`}>
                                    {isChannel ? (
                                        <div className="w-full h-full bg-indigo-600 flex items-center justify-center text-white">
                                            <ChannelGlyph kind={channel?.kind || activeChat.kind} visibility={channel?.visibility} size={isFloating ? 15 : 18} />
                                        </div>
                                    ) : activeChat.photo ? (
                                        <img src={activeChat.photo} className="w-full h-full object-cover" alt={activeChat.name} />
                                    ) : activeChat.type === 'group' || activeChat.isGroup ? (
                                        <div className="w-full h-full bg-emerald-600 flex items-center justify-center text-white">
                                            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>groups</span>
                                        </div>
                                    ) : (
                                        <img src={`https://ui-avatars.com/api/?name=${activeChat.name}&background=6366f1&color=fff`} className="w-full h-full object-cover" alt={activeChat.name} />
                                    )}
                                </div>
                                {activeChat.status === "online" && !(activeChat.type === 'group' || activeChat.isGroup) && (
                                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></div>
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-bold text-slate-800 truncate">{channel?.name || activeChat.name}</h3>
                                <p className="text-xs font-medium leading-tight truncate">
                                    {isChannel ? (
                                        <span className="text-slate-400">
                                            {channelMemberCount} members
                                            {channel?.postingPolicy === 'admins' ? ' · only admins post' : ''}
                                            {channel?.archived ? ' · archived' : ''}
                                        </span>
                                    ) : activeChat.type === 'group' || activeChat.isGroup ? (
                                        <span className="text-slate-400">{activeChat.memberCount || ''} members</span>
                                    ) : activeChat.status === 'online' ? (
                                        <span className="text-emerald-500 font-medium">Online</span>
                                    ) : (
                                        <span className="text-slate-400">Offline</span>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* flex-shrink-0 so every control stays reachable, and a tighter
                            gap because a docked window has no room for gap-4. */}
                        <div className={`flex items-center flex-shrink-0 ${isFloating ? 'gap-0.5' : 'gap-1.5'}`}>
                            {/* Calls are one-to-one. They used to render for groups too, where
                                they did nothing — and a channel would have passed the old
                                `type !== 'group'` check and "called" the channel id as a person. */}
                            {!isMultiPerson && (
                                <>
                                    <button onClick={(e) => {
                                        e.stopPropagation();
                                        startCall?.(activeChat.targetUserId || activeChat.id, 'video');
                                    }} className={`${isFloating ? 'p-1' : 'p-2'} text-slate-400 hover:bg-slate-100 rounded-full transition-all`} title="Video Call">
                                        <span className="material-symbols-outlined">videocam</span>
                                    </button>
                                    <button onClick={(e) => {
                                        e.stopPropagation();
                                        startCall?.(activeChat.targetUserId || activeChat.id, 'voice');
                                    }} className={`${isFloating ? 'p-1' : 'p-2'} text-slate-400 hover:bg-slate-100 rounded-full transition-all`} title="Voice Call">
                                        <span className="material-symbols-outlined">call</span>
                                    </button>
                                    <div className="w-px h-6 bg-slate-200 mx-1"></div>
                                </>
                            )}

                            {/* Search (Hide in floating mode) */}
                            {!isFloating && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); searchOpen ? closeSearch() : setSearchOpen(true); }}
                                    className={`p-2 rounded-full transition-all ${searchOpen ? 'bg-indigo-50 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`}
                                    title="Search this conversation"
                                    aria-label="Search this conversation"
                                    aria-pressed={searchOpen}
                                >
                                    <span className="material-symbols-outlined">search</span>
                                </button>
                            )}

                            {/* Theme Picker (Hide in floating mode) */}
                            {!isFloating && (
                                <div className="relative" ref={themePickerRef}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowThemePicker(!showThemePicker); }}
                                        className={`p-2 rounded-full transition-all ${showThemePicker ? 'bg-indigo-50 text-indigo-600' : 'text-slate-400 hover:bg-slate-100'}`}
                                        title="Chat Theme"
                                    >
                                        <span className="material-symbols-outlined">palette</span>
                                    </button>
                                    {showThemePicker && (
                                        <div
                                            className="absolute top-12 right-0 bg-white rounded-3xl shadow-2xl border border-slate-100 p-4 z-50 min-w-[240px]"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <p className="text-xs font-bold text-slate-800 mb-3">Choose Theme</p>
                                        <div className="grid grid-cols-3 gap-3">
                                            {Object.entries(CHAT_THEMES).map(([key, theme]) => (
                                                <button
                                                    key={key}
                                                    onClick={() => handleThemeChange(key)}
                                                    className={`flex flex-col items-center gap-1.5 p-2.5 rounded-2xl transition-all ${chatTheme === key ? 'bg-indigo-50 ring-2 ring-indigo-400 scale-105' : 'hover:bg-slate-50'}`}
                                                >
                                                    <div
                                                        className="w-10 h-10 rounded-xl border-2 border-white shadow-lg"
                                                        style={{ background: theme.gradient || theme.preview }}
                                                    />
                                                    <span className="text-[10px] font-semibold text-slate-600">{theme.label}</span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Wallpaper Section */}
                                        <div className="mt-3 pt-3 border-t border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-1">Wallpaper</p>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => wallpaperInputRef.current?.click()}
                                                    className="flex-1 text-[11px] font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg py-2 px-3 transition-colors"
                                                >
                                                    📷 Upload
                                                </button>
                                                {chatWallpaper && (
                                                    <button
                                                        onClick={clearWallpaper}
                                                        className="text-[11px] font-medium bg-red-50 text-red-500 hover:bg-red-100 rounded-lg py-2 px-3 transition-colors"
                                                    >
                                                        ✕ Clear
                                                    </button>
                                                )}
                                            </div>
                                            {chatWallpaper && (
                                                <div className="mt-2 h-16 rounded-lg overflow-hidden border border-slate-200">
                                                    <img src={chatWallpaper} alt="Wallpaper" className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            ref={wallpaperInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleWallpaperUpload}
                                        />
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Contact Info (Hide in floating mode) */}
                            {!isFloating && (
                                <button onClick={(e) => { e.stopPropagation(); setShowContactInfo(!showContactInfo); }} className={`p-2 rounded-full transition-colors ${showContactInfo ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                                    <MoreVertical size={isFloating ? 17 : 20} />
                                </button>
                            )}

                            {/* Window Controls (Only in floating mode or when specifically passed) */}
                            {(isFloating || onMinimize || onClose) && (
                                !isFloating && <div className="w-px h-6 bg-slate-200 mx-2" />
                            )}

                            {isFloating && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate('/chat', { state: { openChatId: activeChat.id || activeChat.chatId, targetUserId: activeChat.targetUserId, isGroup: activeChat.isGroup || activeChat.type === 'group' } });
                                    }}
                                    className="p-2 rounded-full text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                    title="Enlarge Chat"
                                >
                                    <span className="material-symbols-outlined text-[20px]">open_in_new</span>
                                </button>
                            )}

                            {onMinimize && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onMinimize(); }}
                                    className={`${isFloating ? 'p-1.5' : 'p-2'} rounded-full text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors`}
                                    title="Minimize"
                                >
                                    <Minimize2 size={isFloating ? 17 : 20} />
                                </button>
                            )}

                            {onClose && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                                    className={`${isFloating ? 'p-1.5' : 'p-2'} rounded-full text-slate-500 hover:text-red-500 hover:bg-red-50 transition-colors`}
                                    title="Close"
                                >
                                    <X size={isFloating ? 18 : 20} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* PINNED MESSAGE BAR — Floating Card */}
                    {activeChat?.chatId && (
                        <PinnedMessageBar
                            chatId={activeChat.chatId}
                            onScrollToMessage={scrollToMessage}
                            canUnpin={canPinMessages}
                        />
                    )}

                    {/* SEARCH — the whole history, on the server, members only */}
                    {searchOpen && (
                        <div className="relative z-30 mx-4 mt-2 bg-white rounded-2xl border border-slate-200 shadow-lg" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                                <span className="material-symbols-outlined text-slate-400 text-[20px]">search</span>
                                <input
                                    autoFocus
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Escape') closeSearch(); }}
                                    placeholder='Search messages — use "quotes" for an exact phrase'
                                    aria-label="Search messages in this conversation"
                                    maxLength={200}
                                    className="flex-1 min-w-0 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
                                />
                                {searchState.status === 'loading' && (
                                    <span className="w-4 h-4 border-2 border-indigo-200 border-t-indigo-500 rounded-full animate-spin flex-shrink-0" aria-label="Searching" />
                                )}
                                <button onClick={closeSearch} className="p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100" title="Close search" aria-label="Close search">
                                    <X size={16} />
                                </button>
                            </div>
                            {searchText.trim() && searchState.status !== 'idle' && (
                                <div className="max-h-72 overflow-y-auto custom-scrollbar" role="listbox" aria-label="Search results">
                                    {searchState.status === 'error' && (
                                        <p className="px-4 py-3 text-xs text-red-500">{searchState.error}</p>
                                    )}
                                    {searchState.status === 'done' && searchState.hits.length === 0 && (
                                        <p className="px-4 py-3 text-xs text-slate-400">No messages match “{searchText.trim()}”.</p>
                                    )}
                                    {searchState.hits.map(hit => {
                                        const sender = hit.senderId === (currentUser?.uid || currentUser?.id)
                                            ? 'You'
                                            : (allUsers?.find(u => u.id === hit.senderId || u.uid === hit.senderId)?.name || hit.senderName || 'Unknown');
                                        return (
                                            <button
                                                key={hit.id}
                                                role="option"
                                                onClick={() => scrollToMessage(hit.id)}
                                                className="w-full text-left px-4 py-2.5 hover:bg-indigo-50/60 focus:bg-indigo-50 outline-none border-b border-slate-50 last:border-b-0"
                                            >
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <span className="text-xs font-semibold text-slate-700 truncate">{sender}</span>
                                                    <span className="text-[10px] text-slate-400 flex-shrink-0">
                                                        {hit.timestamp ? new Date(hit.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 break-words">
                                                    {hit.type !== 'text' && hit.type ? `[${hit.type}] ` : ''}{hit.excerpt || '—'}
                                                </p>
                                            </button>
                                        );
                                    })}
                                    {searchState.truncated && (
                                        <p className="px-4 py-2 text-[11px] text-slate-400 bg-slate-50">
                                            Showing the newest {searchState.hits.length} of {searchState.total} matches — add a word to narrow it down.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* MESSAGES AREA - Full Height & Scrollable */}
                    <div
                        ref={messagesContainerRef}
                        onScroll={handleMessagesScroll}
                        className={`flex-1 overflow-y-auto custom-scrollbar ${isFloating ? 'px-3 py-3 space-y-3' : 'p-6 space-y-6'}`}
                    >
                        {/* Older history — loads on reaching the top, or on click */}
                        {(hasOlder || loadingOlder) && messages.length > 0 && (
                            <div className="flex justify-center">
                                <button
                                    onClick={handleLoadOlder}
                                    disabled={loadingOlder}
                                    className="text-[11px] font-semibold text-indigo-600 bg-white/90 border border-indigo-100 rounded-full px-3 py-1 shadow-sm hover:bg-indigo-50 disabled:opacity-60"
                                >
                                    {loadingOlder ? 'Loading earlier messages…' : 'Load earlier messages'}
                                </button>
                            </div>
                        )}
                        {!hasOlder && !loadingOlder && messages.length > 0 && !loading && (
                            <p className="text-center text-[10px] text-slate-400 select-none">Start of conversation</p>
                        )}

                        {/* Could not load — most often: removed from the group */}
                        {messagesError && (
                            <div className="flex justify-center">
                                <p className="text-xs text-slate-500 bg-white/90 border border-slate-200 rounded-xl px-4 py-2 shadow-sm">{messagesError}</p>
                            </div>
                        )}

                        {/* Empty State */}
                        {messages.length === 0 && !loading && !messagesError && (
                            <div className="flex flex-col items-center justify-center h-full">
                                <div className={`bg-white/90 backdrop-blur-md rounded-3xl shadow-sm border border-slate-100/50 flex flex-col items-center ${isFloating ? 'px-5 py-5 max-w-[240px]' : 'px-10 py-8 max-w-xs'}`}>
                                    <div className="relative mb-5">
                                        <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-violet-100 rounded-2xl flex items-center justify-center">
                                            <Send size={32} className="text-indigo-500 -rotate-12" />
                                        </div>
                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-400 rounded-full flex items-center justify-center shadow-md">
                                            <span className="text-white text-xs">✓</span>
                                        </div>
                                    </div>
                                    <p className="text-base font-bold text-slate-700 mb-1.5">
                                        Start a conversation
                                    </p>
                                    <p className="text-xs text-slate-400 text-center leading-relaxed">
                                        Send your first message to <span className="font-semibold text-indigo-500">{activeChat?.name}</span> and begin the conversation!
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Message List */}
                        {messages
                            .filter(msg => !msg.deletedFor?.[currentUser?.uid || currentUser?.id])
                            .map((msg) => (
                            <React.Fragment key={msg.id}>
                            <MessageBubble
                                msg={msg}
                                isMe={msg.senderId === (currentUser.uid || currentUser.id)}
                                currentUser={currentUser}
                                activeChatName={activeChat.name}
                                isGroup={activeChat.type === 'group' || activeChat.isGroup}
                                allUsers={allUsers}
                                chatTheme={currentTheme}
                                onReply={setReplyTo}
                                onPin={handlePinMessage}
                                onUnpin={handleUnpinMessage}
                                onTask={handleOpenTaskModal}
                                onDelete={handleDeleteMessage}
                                onReminder={handleReminder}
                                canPin={canPinMessages}
                                onRetry={handleRetryMessage}
                                onReact={handleReaction}
                                onForward={handleForwardMessage}
                            />
                            {isChannel && msg.requiresAck === true && !msg.deleted && (
                                <AckBar
                                    chatId={activeChat.chatId}
                                    msg={msg}
                                    uid={myUid}
                                    isMine={msg.senderId === myUid}
                                    canSeeStatus={msg.senderId === myUid || isChannelAdmin}
                                />
                            )}
                            </React.Fragment>
                        ))}

                        {/* Bottom Anchor */}
                        <div ref={messagesEndRef} className="h-1" />
                    </div>

                    {/* UPLOAD PROGRESS */}
                    {isUploading && <FileUploadProgress progress={uploadProgress} />}


                    {/* TYPING INDICATOR */}
                    {typingUsers.length > 0 && (
                        <div className="absolute bottom-20 left-4 z-40 animate-in fade-in slide-in-from-bottom-2">
                            <div className="bg-white/90 backdrop-blur border border-slate-200 px-3 py-1.5 rounded-full flex items-center gap-2 shadow-sm">
                                <div className="flex gap-1">
                                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" />
                                </div>
                                <span className="text-xs font-medium text-slate-500">
                                    {typingUsers.length === 1 ? 'Someone is typing...' : 'People are typing...'}
                                </span>
                            </div>
                        </div>
                    )}

                {/* MODALS */}
                <TaskCreationModal
                    isOpen={showTaskModal}
                    onClose={() => setShowTaskModal(false)}
                    onCreate={handleCreateTask}
                    initialTitle={selectedMessageForAction?.text?.slice(0, 50)}
                    agents={allUsers}
                    chatPartner={activeChat}
                />

                <ScheduleModal
                    isOpen={showScheduleModal}
                    onClose={() => setShowScheduleModal(false)}
                    onSchedule={handleScheduleConfirm}
                />

                {/* Channel admins: a notice that everyone must acknowledge */}
                {isChannel && isChannelAdmin && !channel?.archived && (
                    <div className={`flex justify-end ${isFloating ? 'px-3' : 'px-6'} pt-1`}>
                        <button onClick={() => setShowAnnounce(true)}
                            className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 rounded-full px-3 py-1">
                            <span className="material-symbols-outlined text-[16px]">campaign</span> Post a notice to acknowledge
                        </button>
                    </div>
                )}
                {showAnnounce && (
                    <AnnounceModal chatId={activeChat.chatId} channelName={channel?.name || activeChat.name} onClose={() => setShowAnnounce(false)} />
                )}

                {/* INPUT AREA (Draggable/Docked) */}
                {isChannel && channel && !canPostInChannel ? (
                    // The database rules refuse the write anyway; this says why up front.
                    <div className="p-4 bg-slate-50 text-center text-slate-500 text-sm border-t border-slate-200 select-none">
                        {channel.archived
                            ? 'This channel is archived. Its messages stay readable.'
                            : `Only channel admins post in #${channel.name}.`}
                    </div>
                ) : permissions.canReplyChat !== false ? (
                    <ChatInput
                        inputText={inputText}
                        handleTyping={handleTyping}
                        sendMessage={sendMessage}
                        isRecording={isRecording}
                        onStartRecording={handleStartRecording}
                        onStopRecording={handleStopRecording}
                        fileInputRef={fileInputRef}
                        handleFileUpload={handleFileUpload}
                        onPasteImage={handlePasteImage}
                        setShowScheduleModal={setShowScheduleModal}
                        replyTo={replyTo}
                        setReplyTo={setReplyTo}
                        activeChatName={activeChat.name}
                    />
                ) : (
                    <div className="p-4 bg-slate-50 text-center text-slate-500 text-sm border-t border-slate-200 select-none">
                        ⚠️ Read-only access. You do not have permission to reply.
                    </div>
                )}

                {/* Hidden File Input (controlled via ref in ChatInput) */}
                <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileUpload}
                />

                {/* DELETE CONFIRMATION MODAL */}
                {deleteConfirmMsg && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl max-w-sm mx-4 animate-in zoom-in-95">
                            <h4 className="font-bold text-slate-800 mb-2">Delete Message?</h4>
                            <p className="text-sm text-slate-500 mb-4">How would you like to delete this message?</p>
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={() => confirmDeleteMessage('for_me')}
                                    className="w-full px-4 py-2 text-sm text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium text-left"
                                >
                                    Delete for me
                                </button>
                                {deleteConfirmMsg.senderId === (currentUser?.uid || currentUser?.id) && (
                                    <button
                                        onClick={() => confirmDeleteMessage('for_everyone')}
                                        className="w-full px-4 py-2 text-sm bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 text-left"
                                    >
                                        Delete for everyone
                                    </button>
                                )}
                                <button
                                    onClick={() => setDeleteConfirmMsg(null)}
                                    className="w-full px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-lg font-medium text-center mt-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            </div>

            {/* INFO PANEL (Right Side) — Group or 1:1 */}
            {showContactInfo && (
                isChannel ? (
                    <ChannelInfoPanel
                        chatId={activeChat.chatId}
                        channel={channel}
                        uid={myUid}
                        isAdmin={isChannelAdmin}
                        isOwner={isChannelOwner}
                        allUsers={allUsers}
                        onClose={() => setShowContactInfo(false)}
                        onLeft={() => { setShowContactInfo(false); onClose?.(); onBack?.(); }}
                    />
                ) : (activeChat?.type === 'group' || activeChat?.isGroup) ? (
                    <GroupInfoPanel
                        activeChat={activeChat}
                        messages={messages}
                        onClose={() => setShowContactInfo(false)}
                        allUsers={allUsers}
                    />
                ) : (
                    <ContactInfoPanel
                        activeChat={activeChat}
                        messages={messages}
                        onClose={() => setShowContactInfo(false)}
                        allUsers={allUsers}
                    />
                )
            )}
        </div>
    );
};

export { ChatWindow };
